import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  Settings,
  Database,
  Users,
  Activity,
  CheckCircle2,
  Clock,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { useApp } from '@/contexts/AppContext';
import { formatDate } from '@/lib/formatting';
import { useQuery } from '@tanstack/react-query';
import { getAllDataSources, getRecentSyncs } from '@/api/services/dataSourceService';

interface AdminDashboardProps {
  onNavigate?: (page: string, data?: { highlightSourceType?: string }) => void;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ onNavigate }) => {
  const { hasPermission } = usePermissions();
  const { mode } = useApp();
  const isDemo = mode === 'demo';

  const { data: dataSourcesResponse, isLoading: dataSourcesLoading } = useQuery({
    queryKey: ['admin-dashboard-data-sources'],
    queryFn: getAllDataSources,
    enabled: !isDemo,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 60 * 1000,
  });

  const { data: recentSyncsResponse, isLoading: recentSyncsLoading } = useQuery({
    queryKey: ['admin-dashboard-recent-syncs'],
    queryFn: getRecentSyncs,
    enabled: !isDemo,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 60 * 1000,
  });

  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 30_000);
    return () => clearInterval(interval);
  }, []);

  const liveActivity = (recentSyncsResponse?.items ?? []).map((item, index) => ({
    id: String(index + 1),
    action: `Data source sync completed: ${item.dataSourceName}`,
    user: item.triggerType === 'Manual' && item.triggeredBy ? item.triggeredBy : 'System',
    timestamp: new Date(item.completedAt),
  }));

  const liveDataSources = dataSourcesResponse?.data ?? [];
  const liveConnectedCount = liveDataSources.filter(ds => {
    const s = ds.status.toLowerCase();
    return s === 'connected' || s === 'active' || s === 'pending';
  }).length;
  const liveTotalCount = liveDataSources.length;
  const liveAllConnected = liveConnectedCount === liveTotalCount && liveTotalCount > 0;

  const lastSyncSource = React.useMemo(() => {
    if (!dataSourcesResponse?.data?.length) return null;

    let latest: { name: string; lastSyncDateTime: Date } | null = null;

    for (const ds of dataSourcesResponse.data) {
      if (!ds.lastSyncDateTime) continue;
      const dt = new Date(ds.lastSyncDateTime);
      if (!latest || dt.getTime() > latest.lastSyncDateTime.getTime()) {
        latest = { name: ds.name, lastSyncDateTime: dt };
      }
    }

    return latest;
  }, [dataSourcesResponse]);

  const systemStatus = {
    dataSourcesConnected: isDemo ? 5 : liveConnectedCount,
    totalDataSources: isDemo ? 5 : liveTotalCount,
    lastSync: isDemo ? new Date(Date.now() - 15 * 60 * 1000) : lastSyncSource?.lastSyncDateTime ?? null
  };

  const recentActivity = [
    {
      id: '1',
      action: 'System terminology updated',
      user: 'David Chen',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
    },
    {
      id: '2',
      action: 'New user added: Priya Patel',
      user: 'David Chen',
      timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000),
    },
    {
      id: '3',
      action: 'Data source sync completed',
      user: 'System',
      timestamp: new Date(Date.now() - 15 * 60 * 1000),
    },
    {
      id: '4',
      action: 'User permissions updated',
      user: 'David Chen',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
    },
    {
      id: '5',
      action: 'Data source sync updated',
      user: 'David Chen',
      timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    },
  ];

  const formatDateTime = (date: Date) => {
    const datePart = formatDate(date, undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
    const timePart = formatDate(date, undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${datePart} ${timePart}`;
  };

  const formatTimeAgo = (date: Date) => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  // Check if user has any admin dashboard card permissions
  const hasAnyAdminCard = hasPermission('view_data_sources_card') ||
    hasPermission('view_last_sync_card') ||
    hasPermission('view_recent_activity_feed');

  // Show welcome message if no admin cards are permitted
  if (!hasAnyAdminCard) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="max-w-2xl text-center space-y-4">
            <h2 className="text-3xl font-semibold tracking-tight">
              Welcome to Cash Forecaster
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              You don't have access to any Dashboard content, speak to your system administrator if you think you should. You can access your allowed content using the Navigation pane on the left.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          System configuration and management
        </p>
      </div>

      {/* System Status - Only show cards user has permission for */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {hasPermission('view_data_sources_card') && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Database className="h-4 w-4" />
                Data Sources
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {dataSourcesLoading && !isDemo ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>{systemStatus.dataSourcesConnected}/{systemStatus.totalDataSources}</>
                )}
              </div>
              {(isDemo || liveAllConnected) ? (
                <div className="flex items-center gap-1 text-xs text-green-600 mt-1">
                  <CheckCircle2 className="h-3 w-3" />
                  <span>All connected</span>
                </div>
              ) : !isDemo && liveTotalCount === 0 ? (
                <div className="text-xs text-muted-foreground mt-1">
                  No data sources configured
                </div>
              ) : (
                <div className="flex items-center gap-1 text-xs text-amber-600 mt-1">
                  <AlertCircle className="h-3 w-3" />
                  <span>{liveConnectedCount} of {liveTotalCount} connected</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {hasPermission('view_last_sync_card') && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Last Sync
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!isDemo && dataSourcesLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">Loading...</span>
                </div>
              ) : systemStatus.lastSync ? (
                <>
                  <div className="text-2xl font-bold">{formatTimeAgo(systemStatus.lastSync)}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {isDemo ? 'All sources synchronized' : lastSyncSource?.name}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-2xl font-bold text-muted-foreground">No syncs yet</div>
                  <div className="text-xs text-muted-foreground mt-1">Awaiting first sync</div>
                </>
              )}
            </CardContent>
          </Card>
        )}



      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => onNavigate?.('admin-users')}
            >
              <Users className="h-5 w-5" />
              <span>Manage Users</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => onNavigate?.('admin-data-sources')}
            >
              <Database className="h-5 w-5" />
              <span>Configure Data Sources</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2"
              onClick={() => onNavigate?.('admin-terminology')}
            >
              <Settings className="h-5 w-5" />
              <span>System Settings</span>
            </Button>

          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      {hasPermission('view_recent_activity_feed') && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!isDemo && recentSyncsLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-3">
                {(isDemo ? recentActivity : liveActivity).map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-start justify-between p-3 border rounded-lg hover:bg-accent transition-colors"
                  >
                    <div className="flex-1">
                      <div className="font-medium">{activity.action}</div>
                      <div className="text-sm text-muted-foreground">
                        by {activity.user} at {formatDateTime(activity.timestamp)}
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {formatTimeAgo(activity.timestamp)}
                    </div>
                  </div>
                ))}
                {!isDemo && liveActivity.length === 0 && !recentSyncsLoading && (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    No sync activity yet
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
